// @ts-nocheck
import { Component } from "@theme/component";

/** EU country codes for IP detection */
const EU_COUNTRIES = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE", "GB", "NO", "CH", "IS",
];

/**
 * Format cents as integer-rounded money (drops cents/decimals to match
 * the rest of the theme's `divided_by: 100 | times: 100 | money_without_trailing_zeros`
 * pattern).
 * @param {number} cents
 * @param {Object} [options] - forwarded to window.formatMoneyWithCurrency
 */
function fmtMoneyInt(cents, options = {}) {
  if (typeof window.formatMoneyWithCurrency !== "function") return "";
  const floored = Math.floor(cents / 100) * 100;
  return window
    .formatMoneyWithCurrency(floored, options)
    .replace(/[.,]0{2}(?!\d)/, "");
}

/**
 * Helper to parse money string to number
 * @param {string} moneyStr
 */
function parseMoneyString(moneyStr) {
  if (typeof moneyStr !== "string") return 0;
  let cleanStr = moneyStr.replace(/[^\d,.\-]/g, "");
  if (!cleanStr) return 0;
  const isNegative = cleanStr.startsWith("-");
  cleanStr = cleanStr.replace(/-/g, "");
  const lastComma = cleanStr.lastIndexOf(",");
  const lastDot = cleanStr.lastIndexOf(".");
  if (lastComma !== -1 && lastDot !== -1) {
    if (lastComma > lastDot) {
      cleanStr = cleanStr.replace(/\./g, "").replace(/,/, ".");
    } else {
      cleanStr = cleanStr.replace(/,/g, "");
    }
  } else if (lastComma !== -1) {
    cleanStr = cleanStr.replace(/,/, ".");
  } else {
    const parts = cleanStr.split(".");
    if (parts.length > 2) {
      cleanStr = parts.slice(0, -1).join("") + "." + parts[parts.length - 1];
    }
  }
  let result = parseFloat(cleanStr);
  if (isNaN(result)) result = 0;
  return isNegative ? -result : result;
}

export default class ModulePayV1 extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {(() => void) | null} */
  #boundClickHandler = null;

  /** @type {(() => void) | null} */
  #boundCurrencyHandler = null;

  connectedCallback() {
    super.connectedCallback();

    // Bind handlers for cleanup
    this.#boundClickHandler = this.handleDelegatedClick.bind(this);
    this.#boundCurrencyHandler = () => {
      this.initProgressBars();
      this.initPriceDisplay();
    };

    this.initIPDetection();
    this.setupSelects();
    this.addEventListener("click", this.#boundClickHandler);

    // Initial setup
    this.initProgressBars();
    this.initPriceDisplay();

    // Listen for currency changes if applicable
    document.addEventListener("currency:changed", this.#boundCurrencyHandler);
  }

  updatedCallback() {
    super.updatedCallback();
    this.setupSelects();
    this.initPriceDisplay();
    this.initProgressBars();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Abort any pending fetch requests
    this.#abortController?.abort();

    // Remove event listeners
    if (this.#boundClickHandler) {
      this.removeEventListener("click", this.#boundClickHandler);
    }
    if (this.#boundCurrencyHandler) {
      document.removeEventListener("currency:changed", this.#boundCurrencyHandler);
    }
  }

  /**
   * @param {{ target: { closest: (arg0: string) => any; }; stopPropagation: () => void; preventDefault: () => void; }} e
   */
  handleDelegatedClick(e) {
    // Discount Toggle
    const toggle = e.target.closest(".discount-toggle");
    if (toggle) {
      e.stopPropagation();
      e.preventDefault();
      this.handleDiscountToggle(toggle);
      return;
    }

    // Close Popups when clicking outside
    if (
      !e.target.closest(".discount-popup") &&
      !e.target.closest(".discount-toggle")
    ) {
      this.querySelectorAll(".discount-popup").forEach((p) =>
        p.classList.add("hidden")
      );
    }

    // Media Toggles
    const mediaToggle = e.target.closest(".media-toggle");
    if (mediaToggle) {
      this.handleMediaToggle(mediaToggle);
    }
  }

  initIPDetection() {
    if (this.dataset.shopIp !== "true") {
      this.classList.remove("loading");
      return;
    }

    const CONFIG = {
      CACHE_DURATION: 3600000,
      STORAGE_KEY: "module_pay_ip_v1",
      TARGET: this.dataset.shopIpCountry,
    };

    this.classList.add("loading");

    const checkVisibility = (/** @type {string} */ countryCode) => {
      let show = true;
      if (CONFIG.TARGET === "US") {
        show = countryCode === "US";
      } else if (CONFIG.TARGET === "EU") {
        show = EU_COUNTRIES.includes(countryCode);
      }

      this.classList.toggle("hidden-by-ip", !show);
      this.classList.remove("loading");
    };

    try {
      const cached = JSON.parse(localStorage.getItem(CONFIG.STORAGE_KEY));
      if (cached && Date.now() - cached.timestamp < CONFIG.CACHE_DURATION) {
        checkVisibility(cached.country);
        return;
      }
    } catch (e) { }

    // Create abort controller for fetch cleanup
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    fetch("https://api.ipify.org?format=json", { signal })
      .then((r) => r.json())
      .then((d) => fetch(`https://api.ip.sb/geoip/${d.ip}`, { signal }))
      .then((r) => r.json())
      .then((geo) => {
        if (geo && geo.country_code) {
          localStorage.setItem(
            CONFIG.STORAGE_KEY,
            JSON.stringify({ country: geo.country_code, timestamp: Date.now() })
          );
          checkVisibility(geo.country_code);
        } else {
          throw new Error("Invalid Geo Data");
        }
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          console.error("IP Detect Error", e);
        }
        this.classList.remove("loading");
      });
  }

  handleMediaToggle(toggle) {
    const targetType = toggle.dataset.target;
    const container = toggle.closest(".relative"); // Assuming structure matches
    if (!container) return;

    // Toggle Content
    container.querySelectorAll(".media-content").forEach((content) => {
      const isMatch = content.dataset.type === targetType;
      content.classList.toggle("hidden", !isMatch);
      content.classList.toggle("active", isMatch);

      if (isMatch && targetType === "video") {
        const video = content.querySelector("video");
        if (video)
          setTimeout(
            () =>
              video.play().catch((e) => console.log("Auto-play blocked", e)),
            100
          );
      } else if (!isMatch && content.dataset.type === "video") {
        const video = content.querySelector("video");
        if (video) video.pause();
      }
    });

    // Toggle Buttons Style
    container.querySelectorAll(".media-toggle").forEach((btn) => {
      const isTarget = btn.dataset.target === targetType;
      // Using tailwind classes directly as in original
      btn.classList.toggle("!bg-[#1C3C57]", isTarget);
      btn.classList.toggle("!text-white", isTarget);
      btn.classList.toggle("!bg-gray-100", !isTarget);
      btn.classList.toggle("!text-gray-600", !isTarget);
    });
  }

  handleDiscountToggle(toggle) {
    let container =
      toggle.closest(".price-wrapper-inner") || toggle.parentElement;
    let popup = container.querySelector(".discount-popup");

    this.querySelectorAll(".discount-popup").forEach((p) => {
      if (p !== popup) p.classList.add("hidden");
    });

    if (popup) {
      popup.classList.toggle("hidden");
    }
  }

  setupSelects() {
    this.querySelectorAll("select-component").forEach((select) => {
      if (select.__payV1Bound) return;
      select.__payV1Bound = true;
      select.addEventListener(
        "select-change",
        this.handleSelectChange.bind(this)
      );
    });
  }

  handleSelectChange(e) {
    const { data, selectedItem } = e.detail;
    const variantId = data.variantId;

    // Merge extra data from dataset if needed (e.g. eventDiscountPrice)
    // select.js extracts specific fields into `data`, but not all custom ones
    const mergedData = {
      ...data,
      eventDiscountPrice: selectedItem.dataset.eventDiscountPrice,
    };

    const container = e.target.closest(".sv1-pd");
    this.updateUIFromVariantData(container, mergedData, variantId);
  }

  updateUIFromVariantData(container, data, variantId) {
    // Update Image
    const variantImageContainer = container.querySelector(
      ".variant-image-container .variant-image"
    );
    if (variantImageContainer && data.image) {
      variantImageContainer.src = data.image;
      variantImageContainer.srcset = data.image;
    }

    // Update Discount Badge
    const discountBadge = container.querySelector(
      ".variant-image-container .discount-percentage-badge"
    );
    if (discountBadge) {
      const discountPercentage = data.discountPercentage || 0;
      if (discountPercentage > 0) {
        discountBadge.textContent = `-${discountPercentage}%`;
        discountBadge.classList.remove("hidden");
        discountBadge.setAttribute(
          "data-discount-percentage",
          discountPercentage
        );
      } else {
        discountBadge.classList.add("hidden");
        discountBadge.setAttribute("data-discount-percentage", "0");
      }
    }

    // Update Add to Cart Media
    const addToCartComp = container.querySelector("add-to-cart-component");
    if (addToCartComp && data.image) {
      addToCartComp.dataset.productVariantMedia = data.image;
    }

    // Update Hidden Input (Crucial for ProductFormComponent)
    const productFormInput = container.querySelector(
      'product-form-component input[name="id"]'
    );
    if (productFormInput && variantId) {
      productFormInput.value = variantId;
    }

    // Update Payment Terms Input
    const paymentTermsInput = container.querySelector(
      'payment-terms input[name="id"]'
    );
    if (paymentTermsInput && variantId) {
      paymentTermsInput.value = variantId;
    }

    // Update Price & Progress
    this.updatePriceDOM(container, data);
    this.updateProgressBar(container, data.price || data.comparePrice);
  }

  initPriceDisplay() {
    this.querySelectorAll(".sv1-pd").forEach((container) => {
      const selectItem =
        container.querySelector("select-item[selected]") ||
        container.querySelector("select-item");
      if (selectItem) {
        this.updateUIFromVariantData(
          container,
          selectItem.dataset,
          selectItem.getAttribute("data-variant-id")
        );
        return;
      }

      // Fallback: no select-item rendered (e.g., all variants unavailable).
      // Build minimal data from the existing price-container DOM so the JS
      // layout (Final Price / MSRP / popup) still applies.
      const priceContainer = container.querySelector(".price-container");
      if (!priceContainer) return;
      const currentEl = priceContainer.querySelector(".current-price");
      const compareEl = priceContainer.querySelector(".compare-price");
      const data = {
        price: currentEl ? currentEl.textContent.trim() : "",
        comparePrice: compareEl ? compareEl.textContent.trim() : "",
        discountPercentage: 0,
      };
      this.updatePriceDOM(container, data);
      this.updateProgressBar(container, data.price || data.comparePrice);
    });
  }

  // --- Price Logic (Ported) ---
  calculateFinalPrice(variantPrice, targetPricesStr, discountsStr) {
    if (!targetPricesStr || !discountsStr) return variantPrice;

    const targetPrices = targetPricesStr
      .split(",")
      .map((p) => parseMoneyString(window.formatMoneyWithCurrency(p * 100)));
    const discounts = discountsStr
      .split(",")
      .map((d) => parseMoneyString(window.formatMoneyWithCurrency(d * 100)));

    let finalPrice = variantPrice;
    let maxDiscount = 0;

    if (discounts.length > 0) {
      for (let i = 0; i < targetPrices.length; i++) {
        if (targetPrices[i] <= variantPrice) {
          if (discounts[i] > maxDiscount) {
            maxDiscount = discounts[i];
            finalPrice = variantPrice - discounts[i];
          }
        }
      }
    }
    return finalPrice;
  }

  updatePriceDOM(container, data) {
    const priceContainer = container.querySelector(".price-container");
    if (!priceContainer) return;

    const variantPrice = parseMoneyString(data.price);
    const comparePriceStr = data.comparePrice;
    const comparePrice =
      comparePriceStr && comparePriceStr !== data.price
        ? parseMoneyString(comparePriceStr)
        : 0;

    const selectContent = container.querySelector("select-content");
    const targetPricesStr = selectContent
      ? selectContent.dataset.targetPrices
      : "";
    const discountsStr = selectContent ? selectContent.dataset.discounts : "";

    // Global tier coupon settings (from settings_schema.json -> 阶梯优惠券)
    const tierThresholdsStr = this.dataset.tierThresholds || "";
    const tierDiscountsStr = this.dataset.tierDiscounts || "";
    const tierCouponName = this.dataset.tierCouponName || "";

    const eventDiscountPriceCents = data.eventDiscountPrice
      ? parseFloat(data.eventDiscountPrice)
      : 0;
    const eventDiscountPrice =
      eventDiscountPriceCents > 0 ? eventDiscountPriceCents / 100 : 0;

    const config = {
      showDiscountInfo: priceContainer.dataset.showDiscountInfo === "true",
      discountBadgeText:
        priceContainer.dataset.discountBadgeText ||
        priceContainer.dataset.eventValue,
      discountPriceNumber: eventDiscountPrice,
      discountTitle:
        priceContainer.dataset.discountPriceTitle || "Instant Savings",
      tierCouponName,
    };

    const finalPrice = this.calculateFinalPrice(
      variantPrice,
      targetPricesStr,
      discountsStr
    );
    const hasCouponDiscount = finalPrice !== variantPrice && finalPrice > 0;

    // 全局阶梯优惠券计算（show_discount_info 的 true/false 两种情况都会用到）
    const tierFinalPrice = this.calculateFinalPrice(
      variantPrice,
      tierThresholdsStr,
      tierDiscountsStr
    );
    const tierDiscountAmount = Math.max(0, variantPrice - tierFinalPrice);
    const hasTierDiscount = tierDiscountAmount > 0 && tierFinalPrice > 0;
    config.tierDiscountAmount = tierDiscountAmount;

    let html = "";

    if (config.showDiscountInfo) {
      html = this.renderDiscountInfoLayout(
        variantPrice,
        comparePrice,
        finalPrice,
        hasCouponDiscount,
        config
      );
    } else {
      if (hasTierDiscount) {
        html = this.renderTierCouponLayout(
          variantPrice,
          tierFinalPrice,
          comparePrice,
          config.discountBadgeText,
          config.tierCouponName
        );
      } else if (hasCouponDiscount) {
        html = this.renderCouponLayout(
          variantPrice,
          comparePrice,
          finalPrice,
          config.discountBadgeText
        );
      } else {
        html = this.renderStandardLayout(
          data.price,
          comparePriceStr,
          data.discountPercentage,
          config.discountBadgeText
        );
      }
    }

    priceContainer.innerHTML = html;
  }

  renderDiscountInfoLayout(
    variantPrice,
    comparePrice,
    finalPrice,
    hasCouponDiscount,
    config
  ) {
    const tierDiscount =
      config.tierDiscountAmount > 0 ? config.tierDiscountAmount : 0;
    const customDiscount = tierDiscount;
    let displayFinal = variantPrice - customDiscount;

    const savedAmount = variantPrice - displayFinal;
    const fmtFinal = fmtMoneyInt(displayFinal * 100, { enableConversion: false });
    const fmtMSRP = fmtMoneyInt(variantPrice * 100, { enableConversion: false });
    const fmtSaved = fmtMoneyInt(savedAmount * 100, { enableConversion: false });
    const fmtTier = fmtMoneyInt(tierDiscount * 100, { enableConversion: false });

    const renderPopup = () => {
      const parts = [];
      parts.push(
        `<div><div class="text-xl font-bold border-b border-gray-300 pb-1 mb-1">${fmtFinal}</div><div class="text-xs text-gray-500">Final Price</div></div>`
      );
      parts.push(`<div class="font-bold text-xl">=</div>`);
      parts.push(
        `<div><div class="text-xl font-bold border-b border-gray-300 pb-1 mb-1">${fmtMSRP}</div><div class="text-xs text-gray-500">MSRP</div></div>`
      );

      if (tierDiscount > 0) {
        parts.push(`<div class="font-bold text-xl">-</div>`);
        parts.push(
          `<div><div class="text-xl font-bold border-b border-gray-300 pb-1 mb-1">${fmtTier}</div><div class="text-xs text-gray-500">${config.tierCouponName || "Coupon"}</div></div>`
        );
      }

      return `
        <div class="discount-popup hidden absolute z-50 bg-white text-gray-900 p-4 rounded-lg shadow-2xl border border-gray-200 w-max left-0 -translate-x-[105px]" style="top: 100%; margin-top: 0.5rem;">
            <div class="absolute bottom-full left-1/2 -translate-x-1/2 border-8 border-transparent border-b-gray-200"></div>
            <div class="flex items-center justify-between text-center gap-2">
                ${parts.join("")}
            </div>
        </div>`;
    };

    if (customDiscount > 0) {
      return `
         <div class="flex flex-col gap-2 price-wrapper-inner relative">
            <div class="flex items-center gap-8">
               <div class="flex flex-col gap-0">
                  <span class="text-sm text-gray-500 font-medium">Final Price</span>
                  <span class="text-2xl font-bold text-gray-900">${fmtFinal}</span>
               </div>
               <div class="flex flex-col relative gap-0" style="border-left: 1px solid #e5e7eb; padding-left: 2rem;">
                  <span class="text-sm text-gray-500 font-medium">MSRP</span>
                  <span class="text-xl font-bold text-gray-900">${fmtMSRP}</span>
               </div>
            </div>
            ${savedAmount > 0
          ? `<div class="discount-toggle w-max cursor-pointer bg-[#C41E3A] text-white text-center font-bold py-1.5 px-4 rounded-md text-sm flex items-center justify-center gap-1">
               saved ${fmtSaved}
               <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
            </div>`
          : ""
        }
            ${renderPopup()}
         </div>`;
    } else {
      return `
         <div class="flex flex-col gap-1 price-wrapper-inner relative">
            <span class="text-sm text-gray-500 font-medium">Final Price</span>
            <div class="flex items-baseline gap-3">
               <span class="text-3xl font-bold text-gray-900">${fmtFinal}</span>
            </div>
         </div>`;
    }
  }

  renderCouponLayout(variantPrice, comparePrice, finalPrice, badgeText) {
    let discountPct;
    const baseForCalc =
      comparePrice > variantPrice ? comparePrice : variantPrice;
    discountPct = Math.max(
      0,
      Math.round(((baseForCalc - finalPrice) / baseForCalc) * 100)
    );

    const fmtFinal = fmtMoneyInt(finalPrice * 100, { enableConversion: false });
    const fmtCurrent = fmtMoneyInt(variantPrice * 100, { enableConversion: false });
    const fmtCompare =
      comparePrice > 0
        ? fmtMoneyInt(comparePrice * 100, { enableConversion: false })
        : "";

    return `
      <div class="flex items-center gap-3 flex-wrap">
        <span class="current-price text-xl md:text-2xl font-bold text-gray-900">${fmtCurrent}</span>
        ${comparePrice > variantPrice
        ? `<span class="compare-price text-base md:text-lg text-gray-500 line-through">${fmtCompare}</span>`
        : ""
      }
        <span class="discount-badge bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs md:text-sm font-medium">-${discountPct}%</span>
        ${badgeText
        ? `<span class="discount-tags bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs md:text-sm font-medium">${badgeText}</span>`
        : ""
      }
      </div>
      <span class="final-price inline-block bg-orange-500 text-white px-3 py-1 rounded-md text-sm font-bold mt-2">SALE PRICE ${fmtFinal}</span>
    `;
  }

  /**
   * Show tier-coupon discounted price on the LEFT and current variant price on the RIGHT.
   * Used when show_discount_info=false and a global 阶梯优惠券 discount applies.
   */
  renderTierCouponLayout(
    variantPrice,
    tierFinalPrice,
    comparePrice,
    badgeText,
    couponName
  ) {
    const savedAmount = Math.max(0, variantPrice - tierFinalPrice);

    const fmtDiscounted = fmtMoneyInt(tierFinalPrice * 100, { enableConversion: false });
    const fmtCurrent = fmtMoneyInt(variantPrice * 100, { enableConversion: false });
    const fmtSaved = fmtMoneyInt(savedAmount * 100, { enableConversion: false });

    return `
      <div class="flex items-center gap-3 flex-wrap">
        <span class="tier-discounted-price text-xl md:text-2xl font-bold text-[#C41E3A]">${fmtDiscounted}</span>
        <span class="current-price text-base md:text-lg text-gray-500 line-through">${fmtCurrent}</span>
        ${savedAmount > 0
        ? `<span class="discount-badge bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs md:text-sm font-medium">saved ${fmtSaved}</span>`
        : ""
      }
        ${badgeText
        ? `<span class="discount-tags bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs md:text-sm font-medium">${badgeText}</span>`
        : ""
      }
      </div>
      ${savedAmount > 0
        ? `<div class="tier-coupon-hint text-xs md:text-sm text-[#C41E3A] font-medium mt-1">${couponName || "Coupon"
        }: Save ${fmtSaved}</div>`
        : ""
      }
    `;
  }

  renderStandardLayout(priceStr, compareStr, discountPercentage, badgeText) {
    // Reformat through fmtMoneyInt so display drops cents/decimals consistently
    // even when priceStr came from server-rendered `| money` (with .00).
    const pVal = parseMoneyString(priceStr);
    const cVal = compareStr ? parseMoneyString(compareStr) : 0;
    const showCompare = cVal > pVal;

    const fmtPrice = fmtMoneyInt(pVal * 100, { enableConversion: false });
    const fmtCompare = showCompare
      ? fmtMoneyInt(cVal * 100, { enableConversion: false })
      : "";

    return `
      <div class="flex items-center gap-3 flex-wrap">
         <span class="current-price text-xl md:text-2xl font-bold text-gray-900">${fmtPrice}</span>
         ${showCompare
        ? `
            <span class="compare-price text-base md:text-lg text-gray-500 line-through">${fmtCompare}</span>
            <span class="discount-badge bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs md:text-sm font-medium">-${discountPercentage}%</span>
         `
        : ""
      }
         ${badgeText
        ? `<span class="discount-tags bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs md:text-sm font-medium">${badgeText}</span>`
        : ""
      }
      </div>
    `;
  }

  // --- Progress Bar Logic (Ported) ---
  initProgressBars() {
    this.querySelectorAll(".sv1-pd").forEach((container) => {
      // Logic applied per product block
      const priceContainer = container.querySelector(".price-container");
      if (!priceContainer) return;
      const currentEl = priceContainer.querySelector(".current-price");
      if (currentEl)
        this.updateProgressBar(container, currentEl.textContent.trim());
    });
    this.formatProgressTargets();
  }

  formatProgressTargets() {
    this.querySelectorAll(".formatted-target-price").forEach((el) => {
      const val = parseFloat(el.getAttribute("data-original-price")) || 0;
      if (window.formatMoneyWithCurrency) {
        el.textContent = fmtMoneyInt(val * 100);
      }
    });

    this.querySelectorAll(".milestone-item").forEach((item) => {
      const original = parseFloat(item.getAttribute("data-target-price")) || 0;
      let converted = original;
      if (
        window.Shopify?.currency?.rate &&
        window.Shopify.currency.rate !== 1
      ) {
        converted = Math.round(original * window.Shopify.currency.rate);
      }
      item.setAttribute("data-current-target", converted);
    });
  }

  updateProgressBar(container, currentPriceString) {
    const progressContainer = container.querySelector(
      ".progress-bar-container"
    );
    if (!progressContainer) return;

    const items = progressContainer.querySelectorAll(".milestone-item");
    const fill = progressContainer.querySelector(".progress-fill");
    if (!fill || items.length === 0) return;

    let currentPrice = parseMoneyString(currentPriceString);
    if (window.Shopify?.currency?.rate && window.Shopify.currency.rate !== 1) {
      currentPrice = Math.round(currentPrice * window.Shopify.currency.rate);
    }

    items.forEach((item) =>
      item.classList.remove("completed", "active", "highest-achieved")
    );

    let progressWidth = 0;
    let highestIndex = -1;
    let targetFound = false;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      let targetPrice = parseFloat(
        item.getAttribute("data-current-target") ||
        item.getAttribute("data-target-price")
      );

      if (currentPrice >= targetPrice) {
        item.classList.add("completed");
        highestIndex = i;
        progressWidth = item.offsetLeft + item.clientWidth / 2;
      } else {
        if (!targetFound) {
          item.classList.add("active");
          targetFound = true;
          const prevItem = i > 0 ? items[i - 1] : null;
          const prevPrice = prevItem
            ? parseFloat(
              prevItem.getAttribute("data-current-target") ||
              prevItem.getAttribute("data-target-price")
            )
            : 0;
          const priceProgress =
            (currentPrice - prevPrice) / (targetPrice - prevPrice);

          const startPos = prevItem
            ? prevItem.offsetLeft + prevItem.clientWidth / 2
            : 0;
          const endPos = item.offsetLeft + item.clientWidth / 2;
          progressWidth =
            startPos + Math.max(0, priceProgress) * (endPos - startPos);
        }
        break;
      }
    }

    if (highestIndex >= 0)
      items[highestIndex].classList.add("highest-achieved");
    if (!targetFound) {
      const last = items[items.length - 1];
      progressWidth = last.offsetLeft + last.clientWidth / 2;
    }

    const totalWidth = progressContainer.querySelector(".flex").offsetWidth;
    let percentage = totalWidth > 0 ? (progressWidth / totalWidth) * 100 : 0;
    if (highestIndex === items.length - 1) percentage = 100;

    fill.style.width = Math.min(100, Math.max(0, percentage)) + "%";
  }
}

if (!customElements.get("module-pay-v1")) {
  customElements.define("module-pay-v1", ModulePayV1);
}
